import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import type { PortfolioUserDto } from "@/api/types";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { User } from "@/components/User/User";
import { Badge } from "@/design-system/Badge";
import { Box } from "@/design-system/Box";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Spinner } from "@/design-system/Spinner";
import { Stack } from "@/design-system/Stack";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useOnIntersection } from "@/hooks/useOnIntersection";
import { MIN_GROUP_SEARCH_CHARACTERS } from "@/modules/chats/components/CreateChatModal";
import { usePortfolioChatQueries } from "@/queries/portfolio-chat/queryOptions";
import { useTranslation } from "@/translations";

type CreateChatModalProps = ModalBaseProps & {
  onCreatePrivateChat: (user: PortfolioUserDto) => void;
};

export function CreateChatModal({
  isOpened,
  onOpenChange,
  onCreatePrivateChat,
}: CreateChatModalProps): React.ReactNode {
  const [search, setSearch] = useState<string>("");
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const portfolioChatQueries = usePortfolioChatQueries();
  const debouncedQuery = useDebounceValue(search.trim().length < MIN_GROUP_SEARCH_CHARACTERS ? "" : search);

  const {
    data: availableUsersData,
    hasNextPage: hasMoreUsers,
    fetchNextPage: fetchMoreUsers,
    isLoading: isLoadingUsers,
    isFetchingNextPage: isFetchingMoreUsers,
  } = useInfiniteQuery({
    ...portfolioChatQueries.listAvailableUsersInfinite({
      Search: debouncedQuery,
    }),
    enabled: isOpened,
  });

  const amountTotalUsers = useMemo(() => availableUsersData?.pages[0]?.total ?? 0, [availableUsersData]);
  const availableUsers = useMemo(() => availableUsersData?.pages.flatMap((x) => x.items) ?? [], [availableUsersData]);

  const ref = useOnIntersection({
    threshold: 0,
    onIntersect: useCallback(() => {
      if (!isFetchingMoreUsers && hasMoreUsers) {
        void fetchMoreUsers();
      }
    }, [fetchMoreUsers, hasMoreUsers, isFetchingMoreUsers]),
  });

  return (
    <Modal.Root
      data-testid="portfolio-create-chat-modal"
      title={t("page.chats.modal.new-chat.start-title")}
      {...{
        isOpened,
        onOpenChange,
      }}
      onExitComplete={() => {
        setSearch("");
      }}
    >
      <Modal.Body>
        <Stack gap="md">
          <SearchInput
            data-testid="search-input"
            onChange={(e) => setSearch(e.target.value.trim())}
            placeholder={
              sessionUser.project.type === "addressBased"
                ? t("page.chats.modal.new-chat.input-placeholder.address-based")
                : t("page.chats.modal.new-chat.input-placeholder.company-based")
            }
          />
          <Stack gap="sm">
            {amountTotalUsers > 0 && (
              <span className="text-caption-emphasis">
                {t("page.chats.modal.new-chat.total-results", {
                  count: amountTotalUsers,
                })}
              </span>
            )}
            <PrivateChatUserList
              isLoading={isLoadingUsers}
              users={availableUsers}
              hasMoreUsers={hasMoreUsers}
              loaderRef={ref}
              onSelectUser={onCreatePrivateChat}
            />
          </Stack>
        </Stack>
      </Modal.Body>
    </Modal.Root>
  );
}

type PrivateChatUserListProps = {
  isLoading: boolean;
  users: PortfolioUserDto[];
  hasMoreUsers: boolean;
  loaderRef: (node: Element | null) => void;
  onSelectUser: (user: PortfolioUserDto) => void;
};

function PrivateChatUserList({
  isLoading,
  users,
  hasMoreUsers,
  loaderRef,
  onSelectUser,
}: PrivateChatUserListProps): React.ReactNode {
  const { t } = useTranslation();

  if (isLoading || users.length === 0) {
    return (
      <section className="flex h-scroll-area-compact items-center justify-center">
        {isLoading ? (
          <Spinner size="md" />
        ) : (
          <p className="px-space-md pt-space-xs pb-space-lg text-caption text-text-secondary">
            {t("page.chats.modal.new-chat.no-results")}
          </p>
        )}
      </section>
    );
  }

  return (
    <Card as="ul" density="flush" scroll="compact">
      {users.map((currUser) => (
        <li key={currUser.id} data-testid="available-user" className="border-b border-b-border-default last:border-b-0">
          <button
            type="button"
            onClick={() => onSelectUser(currUser)}
            className="flex w-full cursor-pointer flex-col items-start gap-space-sm p-space-md text-left whitespace-normal text-text-primary focus-visible:ring-2 focus-visible:ring-border-brand focus-visible:outline-hidden"
          >
            <User user={currUser} shouldNavigate={false} />
            {currUser.project && <Badge>{currUser.project?.name}</Badge>}
          </button>
        </li>
      ))}
      {hasMoreUsers && (
        <li className="p-space-md" ref={loaderRef}>
          <Box padding={{ y: "md" }}>
            <Inline justify="center">
              <Spinner size="md" />
            </Inline>
          </Box>
        </li>
      )}
    </Card>
  );
}
